"use client";

import { useRef } from "react";
import { useScroll } from "framer-motion";
import ScrollAnimatedSnakePath from "@/components/common/animations/ScrollAnimatedSnakePath";
import {
  DesktopServiceStepCard,
  ServiceStepData,
} from "@/components/services/ServiceStepCard";
import { MobileSequentialSteps } from "@/components/services/MobileStepWithPath";
import {
  personaDesktopSnakePath,
  personaMobileSnakePaths,
  personaScrollThresholds,
} from "@/data/personaScrollData";

const completeProcessSteps: ServiceStepData[] = [
  {
    title: "Consultation",
    description:
      "We start with a simple phone call to answer any questions you have about the program and verify if your business is eligible for a gaming license.",
    image1: "/pink-flow.jpg",
    image2: "/services/02.jpg",
  },
  {
    title: "Supplying",
    description:
      "Once you secure your Georgia Lottery COAM Class B license, we ensure your location is outfitted with the latest, newest, and greatest high-earning skill games available for the Georgia market. One of our technicians will be happy to help you select the perfect model.",
    image1: "/rounded.jpg",
    image2: "/red-black.webp",
  },
  {
    title: "License Approval",
    description:
      "Getting your GA Lottery COAM Location Class B License is a hassle. Don’t want to deal with the stress of finding rules and regulations by yourself? No need to—our website has all the information conveniently linked for your ease of browsing, to ensure you’re equipped with the proper documentation and fill out your application accurately for speedy approval.",
    image1: "/red-waves.jpg",
    image2: "/pink-wave.webp",
  },
  {
    title: "Set-Up",
    description:
      "Our team of technicians will bring you the latest and best titles for an expert set-up. This includes a quality-assurance check and expert advice if you have questions or concerns. Our primary goal is ensuring your machine is operating perfectly and guests have a place to enjoy game play. We offer 24/7 service so you can schedule a time that works best for your business.",
    image1: "/services/01.jpg",
    image2: "/red-black.webp",
  },
  {
    title: "Continuing Support",
    description:
      "We actively partner with you to grow revenue by analyzing play data, recommending store layout improvements, and regularly updating software to the latest, high-demand games.",
    image1: "/services/03.jpg",
    image2: "/services/05.jpg",
  },
];

export default function CompleteProcessScrollSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end end"],
  });

  return (
    <div className="bg-brand-black relative w-full overflow-visible pt-2 pb-32">
      {/* Header Section */}
      <div className="relative mb-20 flex flex-col items-center justify-center px-5 text-center lg:mb-10 lg:px-[100px]">
        <p className="font-raleway mb-3 text-base text-white/80">
          Superior Service With
        </p>
        <h2 className="font-raleway text-2xl font-bold text-white md:text-[2.5rem]">
          Halcyon Amusements
        </h2>
        <p className="font-raleway mx-auto mt-5 max-w-5xl text-sm leading-[1.3] text-white/80 md:text-base">
          Halcyon Amusements is your go-to for all things Class B COAM (Coin
          Operated Amusement Machines) and Georgia Lottery Licensing. Priding
          ourselves on a trusted, honest, and loyal relationship with our
          retailers, your business&apos; success is our top priority.
        </p>
        <p className="font-raleway mx-auto mt-4 max-w-5xl text-sm leading-[1.3] text-white/80 md:text-base">
          We&apos;ll work hard to jump-start your revenue increase, while
          providing 5-star installation and maintenance service. On top of that,
          we&apos;ll ensure your machine set-up is compliant with current GLC
          regulations. To work with dedicated professionals and receive 24/7
          service, get in touch today.
        </p>
      </div>

      {/* Main Content Area: Snake Path + Steps */}
      <div ref={containerRef} className="relative mt-[10px] w-full">
        {/* Desktop Snake Path */}
        <div className="pointer-events-none absolute top-0 left-0 hidden h-full w-full lg:block">
          <div
            className="mx-auto h-full"
            style={{ maxWidth: "calc(100% - 200px)" }}
          >
            <ScrollAnimatedSnakePath
              pathD={personaDesktopSnakePath}
              scrollYProgress={scrollYProgress}
              strokeWidth={0.05}
              className="h-full w-full"
              viewBox="0 0 100 160"
            />
          </div>
        </div>

        <div className="relative z-10 mx-auto px-5 md:px-12 lg:px-[100px]">
          {/* Desktop Service Steps */}
          <div className="hidden space-y-48 lg:block">
            {completeProcessSteps.map((step, index) => (
              <DesktopServiceStepCard
                key={index}
                step={step}
                index={index}
                scrollYProgress={scrollYProgress}
                thresholds={personaScrollThresholds}
              />
            ))}
          </div>

          {/* Mobile Service Steps with Sequential Animation */}
          <div className="lg:hidden">
            <MobileSequentialSteps
              steps={completeProcessSteps}
              pathDefinitions={personaMobileSnakePaths}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
